// Client-side upload helpers. Type, size and duplicate checks are
// handled by the shared CPLFileUpload component; only the pieces it can't cover live here.

export const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;

export const EXCEL_EXTENSIONS = ["xlsx", "xlsm"];

export enum UploadFileStatus {
  Scanning = 1,
  Scanned,
  Error,
}

// Excel = one workbook (its sheets map to template worksheets); Csv = one file per worksheet.
// Both modes share the same flow — worksheet association happens on the upload screen.
export enum UploadMode {
  Excel = 1,
  Csv,
}

// One lone workbook = excel mode; anything else — CSVs, or an invalid mixed batch — is treated
// as csv mode (the batch-violation error carries the real problem in that case).
export function deriveUploadMode(fileNames: string[]): UploadMode {
  return fileNames.length === 1 && EXCEL_EXTENSIONS.includes(getFileExtension(fileNames[0]))
    ? UploadMode.Excel
    : UploadMode.Csv;
}

export enum UploadErrorKind {
  ScanFailed = 1,
  BatchViolation,
}

export function getFileExtension(fileName: string): string {
  const parts = fileName.split(".");
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : "";
}

// Workbook sheet → template worksheet auto-match: exact on trimmed, case-insensitive names.
// Sheets in a filled-in template keep the template's names,
// so anything looser would only mis-associate stray working tabs.
export function matchWorksheetBySheetName(sheetName: string, worksheets: string[]): string | null {
  const target = sheetName.trim().toLowerCase();
  return worksheets.find((worksheet) => worksheet.trim().toLowerCase() === target) ?? null;
}

// Filename → worksheet auto-suggest for CSV files ("properties_riverside.csv" → "Properties").
// Longest matching sheet name wins so "fund_schedule_billing_period" prefers "FundSchedule Billing Period"
// over "Fund Schedules"; the trailing-s trim lets "client-list" match "Clients".
export function suggestWorksheet(fileName: string, worksheets: string[]): string | null {
  const normalise = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, "");
  const fileNorm = normalise(fileName.replace(/\.[^.]*$/, ""));

  let best: string | null = null;
  let bestLength = 0;

  for (const worksheet of worksheets) {
    const sheetNorm = normalise(worksheet);
    const singular = sheetNorm.replace(/s$/, "");
    if ((fileNorm.includes(sheetNorm) || fileNorm.includes(singular)) && sheetNorm.length > bestLength) {
      best = worksheet;
      bestLength = sheetNorm.length;
    }
  }

  return best;
}
